import { useEffect, useRef, useState } from 'react';
import axiosInstance from 'shared/api/axiosInstance';
import { absoluteAssetUrlForStoredPath } from 'shared/api/services/publicTenantBrandingService';

/**
 * Turns stored photo path into a path segment for axios (baseURL already includes /api/v1).
 * Accepts: /api/v1/files/image/x.png, http://host/api/v1/files/image/x.png, files/image/x.png
 */
function isPublicDiskImagePath(photoUrl) {
  if (!photoUrl || !String(photoUrl).trim()) return false;
  const raw = String(photoUrl).trim();
  let path = raw;
  if (/^https?:\/\//i.test(raw)) {
    try {
      path = new URL(raw).pathname;
    } catch {
      path = raw;
    }
  }
  const lower = path.toLowerCase();
  return lower.includes('/files/image/') || lower.startsWith('files/image/');
}

export function photoUrlToAxiosRelativePath(photoUrl) {
  if (!photoUrl || !String(photoUrl).trim()) return null;
  let path = String(photoUrl).trim();
  if (/^https?:\/\//i.test(path)) {
    try {
      path = new URL(path).pathname;
    } catch {
      return null;
    }
  }
  if (!path.startsWith('/')) path = `/${path}`;
  const prefix = '/api/v1/';
  const lower = path.toLowerCase();
  if (lower.startsWith(prefix)) {
    return path.slice(prefix.length);
  }
  if (lower.startsWith('/files/')) {
    return path.slice(1);
  }
  return null;
}

export function useProfilePhotoState(photoUrl) {
  const [src, setSrc] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const blobUrlRef = useRef(null);
  const rel = photoUrlToAxiosRelativePath(photoUrl);
  const shouldStartLoading = Boolean(rel) && src === undefined && !hasError;

  useEffect(() => {
    let cancelled = false;

    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    setSrc(undefined);
    setIsLoading(false);
    setHasError(false);

    if (!rel) {
      return () => {
        cancelled = true;
        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
          blobUrlRef.current = null;
        }
      };
    }

    setIsLoading(true);

    (async () => {
      try {
        const res = await axiosInstance.get(rel, {
          responseType: 'blob',
          timeout: 25000,
        });
        const ct = (res.headers && res.headers['content-type']) || '';
        if (ct.includes('application/json')) {
          if (!cancelled) {
            setHasError(true);
            setIsLoading(false);
          }
          return;
        }
        if (cancelled) return;
        const url = URL.createObjectURL(res.data);
        blobUrlRef.current = url;
        setSrc(url);
        setHasError(false);
        setIsLoading(false);
      } catch {
        if (!cancelled) {
          setSrc(undefined);
          setHasError(true);
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [photoUrl]);

  return {
    src,
    isLoading: isLoading || shouldStartLoading,
    hasError,
  };
}

/**
 * Loads profile image via axios (sends Authorization + X-TenantID like other API calls)
 * and exposes a blob: URL for MUI Avatar. Avoids broken <img src> when the path is wrong
 * or cross-origin without headers.
 */
export default function useProfilePhotoSrc(photoUrl) {
  // Tenant logos and profile photos are public disk files — use a normal <img src>
  // so login/logout never depends on JWT or X-TenantID.
  const publicUrl = isPublicDiskImagePath(photoUrl)
    ? absoluteAssetUrlForStoredPath(photoUrl)
    : '';
  const blobSrc = useProfilePhotoState(publicUrl ? null : photoUrl).src;
  return publicUrl || blobSrc;
}
